import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./msalConfig";
import { useAuth } from "./AuthContext";
import { authAPI } from "../services/api";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Antigravity from "../components/Antigravity";
import imachLogo from "../assets/login/logo.png";
import imachLogo360 from "../assets/login//iMach_360_logo.png";
import imachLogo360Dark from "../assets/imach360-Alt dark.png";
// import loginIllustration from "../assets/login/login-page-img.jpg";


const Login: React.FC = () => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  // 👇 ADD setAuthFromStorage (NO logic change)
  const { login, setAuthFromStorage } = useAuth();
  
  // Force light mode on login page
  const isDarkMode = false;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Handle Microsoft redirect callback (after returning from Microsoft login)
  useEffect(() => {
    if (accounts.length > 0) {
      // Only proceed if we have accounts and no error (fresh redirect from Microsoft)
      const isReturningFromMicrosoftLogin = sessionStorage.getItem('returning-from-ms-login');
      if (isReturningFromMicrosoftLogin) {
        sessionStorage.removeItem('returning-from-ms-login');
        const handleTokenExchange = async () => {
          try {
            setLoading(true);
            setError("");

            // Use the most recently authenticated account (last in array), not the cached one
            const account = accounts[accounts.length - 1];

            instance.setActiveAccount(account);

            const tokenResponse = await instance.acquireTokenSilent({
              ...loginRequest,
              account,
            });

            // Send Microsoft token to backend for verification and JWT issuance
            const response = await authAPI.microsoftLogin({
              accessToken: tokenResponse.accessToken
            });

            // Store app JWT + user only if they exist
            if (response.data.token && response.data.user) {
              localStorage.setItem("authToken", response.data.token);
              localStorage.setItem("user", JSON.stringify(response.data.user));
              // Update auth context with stored credentials
              setAuthFromStorage();
            } else {
              throw new Error('Invalid response from server: missing token or user data');
            }

            // Clear session storage flag
            sessionStorage.removeItem('returning-from-ms-login');

            navigate("/dashboard", { replace: true });
          } catch (err: any) {
            // Provide helpful error message based on error type
            let errorMessage = "Microsoft authentication failed. Please contact your administrator.";

            if (err?.response?.status === 404 || err?.response?.data?.error === 'USER_NOT_FOUND') {
              errorMessage = `Your account is not registered in the system. Please contact your administrator.`;
            } else if (err?.response?.status === 403) {
              errorMessage = `Your account is inactive. Please contact your administrator.`;
            } else if (err?.message?.includes('Network error')) {
              errorMessage = "Cannot connect to server. Check your connection and backend URL configuration.";
            } else if (err?.message === 'Invalid response from server: missing token or user data') {
              errorMessage = "Server error: Invalid response. Check backend configuration.";
            } else {
              errorMessage = err?.response?.data?.message || err?.message || errorMessage;
            }

            setError(errorMessage);

            // Clear session storage flag on error
            sessionStorage.removeItem('returning-from-ms-login');
          } finally {
            setLoading(false);
          }
        };
        handleTokenExchange();
      }
    }
  }, [accounts, instance, navigate, setAuthFromStorage]);

  // Auto-clear alerts after 5 seconds
  useEffect(() => {
    if (error || successMessage) {
      const timer = setTimeout(() => {
        setError("");
        setSuccessMessage("");
      }, 5000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, successMessage]);

  // Clear alerts when email changes
  useEffect(() => {
    if (email) {
      setError("");
      setSuccessMessage("");
      setEmailError("");
    }
  }, [email]);

  // Clear alerts when password changes
  useEffect(() => {
    if (password) {
      setError("");
      setPasswordError("");
    }
  }, [password]);

  // Email validation function
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError("Email is required");
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  // Password validation function
  const validatePassword = (password: string) => {
    if (!password) {
      setPasswordError("Password is required");
      return false;
    }
    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      return false;
    }
    setPasswordError("");
    return true;
  };

  // EMAIL / PASSWORD LOGIN
  const handleEmailPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate fields before API call
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);

    try {
      await login(email, password);
      window.location.replace('/dashboard');
    } catch (err: any) {
      // Provide user-friendly error message
      let errorMessage = "Login failed";
      if (err?.message === 'Invalid response from server: missing token or user data') {
        errorMessage = "Server error: Invalid response. Check backend configuration.";
      } else if (err?.response?.status === 404) {
        errorMessage = "Account not found. Please contact your administrator.";
      } else if (err?.response?.status === 401) {
        errorMessage = "Invalid email or password.";
      } else if (err?.response?.status === 403) {
        errorMessage = "Account is inactive. Contact your administrator.";
      } else if (err?.message?.includes('Network error')) {
        errorMessage = "Cannot connect to server. Check your connection and API URL configuration.";
      } else {
        errorMessage = err?.message || err?.response?.data?.message || "Login failed";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // FORGOT PASSWORD HANDLER dd
  const handleForgotPassword = async () => {
    // Validate email before proceeding
    const isEmailValid = validateEmail(email);
    if (!isEmailValid) {
      return;
    }

    try {
      setForgotPasswordLoading(true);
      setError("");
      setSuccessMessage("");

      await authAPI.forgotPassword(email);
      setSuccessMessage("Password reset link has been sent to your email address");
    } catch (err: any) {
      let errorMessage = "Failed to send password reset email";

      if (err?.response?.status === 404) {
        errorMessage = "Email address not found in our system";
      } else if (err?.response?.status === 429) {
        errorMessage = "Too many requests. Please try again later";
      } else if (err?.message?.includes('Network error')) {
        errorMessage = "Cannot connect to server. Check your connection";
      } else {
        errorMessage = err?.response?.data?.message || err?.message || errorMessage;
      }

      setError(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // MICROSOFT LOGIN (MSAL + PKCE)
  const handleMicrosoftLogin = async () => {
    try {
      // Clear any previous errors before attempting redirect
      setError("");
      setLoading(false);

      // Clear the active account to force account selection
      instance.setActiveAccount(null);

      // Clear MSAL cache from localStorage to force fresh authentication
      const msalKeys = Object.keys(localStorage).filter(
        key => key.includes('msal') || key.includes('microsoft')
      );
      msalKeys.forEach(key => localStorage.removeItem(key));

      // Clear cached accounts from storage to force fresh authentication
      sessionStorage.clear();

      // Mark that we're initiating a Microsoft login
      sessionStorage.setItem('returning-from-ms-login', 'true');

      // Redirect to Microsoft login page with account selection prompt
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'select_account', // Force account selection page
        extraQueryParameters: {
          'prompt': 'select_account'
        }
      });
    } catch (err) {
      setError("Could not redirect to Microsoft login. Please try again.");
      sessionStorage.removeItem('returning-from-ms-login');
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor={isDarkMode ? '#0f172a' : '#fbfaf9'}
      position="relative"
      sx={{
        overflow: "auto",
      }}
    >

      <Antigravity
        count={200}
        magnetRadius={200}
        particleSize={1.5}
        color="#7036e3"
        fieldStrength={60}
      />

      {/* LOGIN FORM  */}
      <Box
        position="relative"
        zIndex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ xs: 1, sm: 2, md: 3 }}
        py={{ xs: 2, md: 0 }}
        pb={{ xs: 8, sm: 2, md: 0 }}
        width="100%"
      >
        <Box
          width={{ xs: "100%", sm: 480 }}
          maxWidth={{ xs: "100vw", sm: 480 }}
          sx={{
            background: isDarkMode
              ? 'rgba(15, 23, 42, 0.75)'
              : 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '25px',
            border: isDarkMode
              ? '2px solid rgba(148, 163, 184, 0.2)'
              : '2px solid rgba(255, 255, 255, 0.5)',
            padding: { xs: '24px', sm: '40px' },
            boxShadow: isDarkMode
              ? '0 8px 32px 0 rgba(0, 0, 0, 0.6), inset 0 1px 2px rgba(255,255,255,0.05)'
              : '0 8px 32px 0 rgba(31, 38, 135, 0.37), inset 0 1px 2px rgba(255, 255, 255, 0.6)',
            backdropBlur: '10px',
          }}
        >
          {/* Logo */}
          <Box
            display="flex"
            justifyContent="center"
            mb={2}
          >
            <img src={isDarkMode ? imachLogo360Dark : imachLogo360} alt="iMach" height={80} />
          </Box>

          {/* Heading */}
          <Typography
            sx={{
              fontSize: { xs: '20px', sm: '24px' },
              fontWeight: 700,
              color: isDarkMode ? '#f1f5f9' : '#000000',
              mb: 1,
              textAlign: 'center',
            }}
          >
            Innovate, Ideate & Implement
          </Typography>

          <Typography
            variant="h5"
            fontWeight={600}
            gutterBottom
            align="center"
            sx={{ color: isDarkMode ? '#f1f5f9' : '#000000', mb: 1 }}
          >
            Sign In
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? '#94a3b8' : '#374151', mb: 3 }}
            align="center"
          >
            Sign in with your iMach account
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                '& .MuiAlert-message': {
                  color: '#000000'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert
              severity="success"
              sx={{
                mb: 2,
                '& .MuiAlert-message': {
                  color: '#000000'
                }
              }}
            >
              {successMessage}
            </Alert>
          )}

          <form onSubmit={handleEmailPasswordLogin}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => validateEmail(email)}
                fullWidth
                required
                error={!!emailError}
                helperText={emailError}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                    '& fieldset': {
                      borderColor: emailError ? '#f44336' : (isDarkMode ? '#475569' : '#d1d5db'),
                    },
                    '& fieldset legend': {
                      color: isDarkMode ? '#94a3b8' : undefined,
                    },
                    '&:hover fieldset': {
                      borderColor: emailError ? '#f44336' : '#6A3CBC',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: emailError ? '#f44336' : '#6A3CBC',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: emailError ? '#f44336' : (isDarkMode ? '#94a3b8' : '#000000'),
                    '&.Mui-focused': {
                      color: emailError ? '#f44336' : '#6A3CBC',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: isDarkMode ? '#f1f5f9' : '#111827',
                  },
                }}
              />

              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => validatePassword(password)}
                fullWidth
                required
                error={!!passwordError}
                helperText={passwordError}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                    '& fieldset': {
                      borderColor: passwordError ? '#f44336' : (isDarkMode ? '#475569' : '#d1d5db'),
                    },
                    '&:hover fieldset': {
                      borderColor: passwordError ? '#f44336' : '#6A3CBC',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: passwordError ? '#f44336' : '#6A3CBC',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: passwordError ? '#f44336' : (isDarkMode ? '#94a3b8' : '#000000'),
                    '&.Mui-focused': {
                      color: passwordError ? '#f44336' : '#6A3CBC',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: isDarkMode ? '#f1f5f9' : '#111827',
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 1,
                  py: 1.4,
                  background: "linear-gradient(45deg, #9358f7, #6197ee, #10d7e2)",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background: "linear-gradient(45deg, #9358f7, #6197ee, #10d7e2)",
                    filter: "brightness(0.9)",
                  },
                }}
              >
                {loading ? <CircularProgress size={22} /> : "Login"}
              </Button>
            </Stack>
          </form>

          <Divider sx={{ my: 3 }}>
            <Typography sx={{ color: isDarkMode ? '#94a3b8' : '#000000', fontWeight: 500 }}>OR</Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleMicrosoftLogin}
            disabled={loading}
            sx={{
              py: 1.3,
              textTransform: "none",
              borderColor: "#D1D5DB",
              background: "linear-gradient(45deg, #9358f7, #6197ee, #10d7e2)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 1,
              "&:hover": {
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#F3F4F6',
              },
            }}
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
              alt="Microsoft"
              height={18}
            />
            Continue with Microsoft
          </Button>

          <Box display="flex" justifyContent="space-between" mt={3}>
            <Typography
              variant="caption"
              sx={{
                background: "linear-gradient(45deg, #9358f7, #6197ee, #10d7e2)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                cursor: forgotPasswordLoading ? 'not-allowed' : 'pointer',
                textDecoration: forgotPasswordLoading ? 'none' : 'underline',
                textDecorationColor: forgotPasswordLoading ? 'inherit' : '#9358f7',
                fontWeight: forgotPasswordLoading ? 400 : 500,
                '&:hover': forgotPasswordLoading ? {} : {
                  textDecoration: 'none',
                  filter: 'brightness(0.8)'
                }
              }}
              onClick={forgotPasswordLoading ? undefined : handleForgotPassword}
            >
              {forgotPasswordLoading ? 'Sending...' : 'Forgot password?'}
            </Typography>

            <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#374151' }}>
              Contact HR for help
            </Typography>
          </Box>
        </Box>

        {/* Copyright and Powered By - Bottom Right */}
        <Box
          position="fixed"
          bottom={15}
          right={20}
          sx={{
            zIndex: 2,
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.5,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? '#94a3b8' : '#545659',
                fontSize: '12px',
                fontWeight: 500,
              }}
            >
              Powered by
            </Typography>
            <img src={imachLogo} alt="iMach" height={14} style={{ opacity: 0.8 }} />
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? '#94a3b8' : '#545659',
              fontSize: '11px',
              fontWeight: 400,
            }}
          >
            Copyright © 2022 All Rights Reserved passion by iMach
          </Typography>
        </Box>
      </Box>
    </Box>
  );


};

export default Login;
